import { useEffect, useMemo, useState } from 'react'

import * as store from '@store'
import * as actions from '@actions'
import * as halfileActions from '@actions/halfile'

import useSortableTableHeaders from '@/hook/useSortableTableHeaders'
import Pagination from '@/components/shared/Pagination'
import Th from '@/components/shared/TableHeaderManager/Th'
import { createSyntheticDownload, sortBy } from '@/utils'
import { formatDate } from '@/utils/date'
import { MdCopyAll, MdDownload } from 'react-icons/md'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { SelectRunsheetDialog } from '../SelectRunsheetDialog'
import AddOrCreateCollection from '@/components/tract/AddOrCreateCollection'
import { UpsertRunsheet } from '@/components/tract/UpsertRunsheet'
import { useWireValue } from '@forminator/react-wire'
import PaidFeatureBadge from '@/components/shared/PaidFeatureBadge'
import { PURCHASE_TYPE_HALFILE_DOC } from '@/constants'
import usePayments from '@/hook/usePayments'

const headers = [
    ['Grantor', 'Grantor', false, '200px', v => v?.split('|').join('; ')],
    ['Grantee', 'Grantee', false, '200px', v => v?.split('|').join('; ')],
    ['Volume', 'Volume', true, '90px'],
    ['Page', 'Page', true, '90px'],
    ['ClerkNumber', 'Instr. #', true, '90px'],
    ['InstrumentType', 'Instr. Type', true, '140px'],
    ['InstrumentDate', 'Instr. Date', true, '140px', v => formatDate(v, 'MM/DD/YYYY')],
    ['FileDate', 'File Date', true, '140px', v => formatDate(v, 'MM/DD/YYYY')],
    ['Brief_Legal', 'Legal Description', false, '250px', v => v?.split('|').join('; ')],
]

const ITEMS_PER_PAGE = 50

export const DocumentCountySearchTable = ({
    documents,
    setDocuments,
}) => {
    
    const { countyKey } = useParams()
    
    const [pageIndex, setPageIndex] = useState(0)
    const { onHeaderClick, orderDirection, orderField } = useSortableTableHeaders(headers, 'InstrumentDate')
    const [collectionId, setCollectionId] = useState(null)
    
    const [showCollectionModal, setShowCollectionModal] = useState(false)
    const [showRunsheetModal, setShowRunsheetModal] = useState(false)
    const [showCreateRunsheetModal, setShowCreateRunsheetModal] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState(null)
    const [fundingSource, setFundingSource] = useState(null)
    
    const runsheets = useWireValue(store.runsheets)
    const remoteConfig = useWireValue(store.remoteConfig)
    
    const payments = usePayments()
    
    const updateDocumentPurchase = document => {
        const idx = documents.findIndex(d => d.DocumentID === document.DocumentID)
        
        const copy = [...documents]
        
        copy.splice(idx, 1, { ...document, purchased: true })
        
        setDocuments(copy)
    }
    
    const download = async document => {
        
        const handleDownload = async (_, fundingSource) => {
            
            const res = await halfileActions.downloadHalfilePdf(countyKey, document.DocumentID, fundingSource)
            
            try {
                
                createSyntheticDownload(res.data, res.headers['content-type'],
                    `${document.InstrumentNumber || document.DocumentID}.pdf`)
                
                updateDocumentPurchase(document)
            } catch {
                toast.error(`Failed to download document. ${!!fundingSource && 'Payment refunded.'}`)
            } finally {
                if (fundingSource && !remoteConfig?.realtimeEnabled)
                    await actions.updateLocalCredits()
            }
            
        }
        
        if (document.purchased || !remoteConfig.payments.enabled) {
            return handleDownload()
            
        }
        
        payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_HALFILE_DOC,
            itemCount: 1,
            onConfirmPayment: handleDownload,
        })
        
        
    }
    
    const handleAddToCollectionAndRunsheet = document => {
        
        if (document.purchased || !remoteConfig.payments.enabled) {
            setFundingSource(null)
            setSelectedDocument(document)
            setShowCollectionModal(true)
            return
        }
        
        payments.showPurchaseDialog({
            purchaseType: PURCHASE_TYPE_HALFILE_DOC,
            itemCount: 1,
            onConfirmPayment: (_, fundingSource) => {
                setFundingSource(fundingSource)
                setSelectedDocument(document)
                setShowCollectionModal(true)
            },
        })
        
    }
    
    const addToCollectionAndRunsheet = async runsheetId => {
        try {
            const res = await halfileActions.addToCollectionAndRunsheet(countyKey,
                selectedDocument.DocumentID, collectionId, runsheetId, fundingSource)
            
            if (fundingSource && !remoteConfig?.realtimeEnabled)
                await actions.updateLocalCredits()
            
            updateDocumentPurchase(selectedDocument)
            
            toast.success(`Added document ${selectedDocument.ClerkNumber
            } to collection "${res.data.collection.name
            }" and runsheet "${res.data.runsheet.name}".`)
        } catch (err) {
            console.log(err)
            if (err.response.status === 409)
                return toast.warn(err.response.data.message)
            
            toast.error('Failed to add document.')
        }
        
        setSelectedDocument(null)
        setCollectionId(null)
        setFundingSource(null)
    }
    
    useEffect(() => {
        
        setPageIndex(0)
        
    }, [orderField, orderDirection, documents])
    
    useEffect(() => {
        actions.fetchRunsheets()
    }, [])
    
    const sortedDocs = useMemo(() => {
        
        return sortBy(
            [...documents],
            orderField,
            ['InstrumentDate', 'FileDate'].includes(orderField) ? 'date' : 'natural',
            orderDirection === 'ASC',
        ).slice(pageIndex * ITEMS_PER_PAGE, (pageIndex + 1) * ITEMS_PER_PAGE)
        
    }, [pageIndex, orderField, orderDirection, documents])
    
    return (
        <>
            <div>
                <div className="max-h-[calc(100vh-192px)] overflow-auto">
                    <table className="table-sticky-headers">
                        <thead>
                            <tr>
                                <th className="w-10"></th>
                                {headers.map(([slug, label, sortable, width]) =>
                                    <Th
                                        style={{ maxWidth: width, width }}
                                        key={slug}
                                        slug={slug}
                                        label={label}
                                        sortable={sortable}
                                        onHeaderClick={onHeaderClick}
                                        orderField={orderField}
                                        orderDirection={orderDirection} />)}
                            </tr>
                        </thead>
                        <tbody>
                            {sortedDocs.map(sd =>
                                <tr key={sd.DocumentID}>
                                    <td>
                                        <div className="flex join">
                                            <button
                                                className="p-2 btn btn-secondary btn-sm btn-square
                                                    tooltip tooltip-primary tooltip-bottom-right font-normal join-item"
                                                data-tip="Download"
                                                onClick={() => download(sd)}>
                                                <MdDownload />
                                            </button>
                                            <button
                                                className="p-2 btn btn-primary btn-sm btn-square
                                                    tooltip tooltip-primary tooltip-bottom-right font-normal join-item"
                                                data-tip="Add to Collection and Runsheet"
                                                onClick={() => handleAddToCollectionAndRunsheet(sd)}>
                                                <MdCopyAll />
                                                <PaidFeatureBadge show={remoteConfig.payments.enabled
                                                    && !sd.purchased} />
                                            </button>
                                        </div>
                                    </td>
                                    {headers.map(([slug, , , , transform]) => <td key={`${slug}_${sd.DocumentID}`}>
                                        {transform ? transform(sd[slug]) : sd[slug]}
                                    </td>)}
                                </tr>,
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="pt-4 text-center">
                    <Pagination
                        count={documents?.length || 0}
                        itemsPerPage={ITEMS_PER_PAGE}
                        setPageIndex={setPageIndex}
                        forcePage={pageIndex} />
                </div>
            </div>
            <AddOrCreateCollection
                showAddCollection={showCollectionModal}
                setShowAddCollection={setShowCollectionModal}
                collectionId={collectionId}
                setCollectionId={setCollectionId}
                uploadedFiles={[0]}
                setUploadedFiles={() => { }}
                setShowUploadPopUp={setShowRunsheetModal}/>
            <SelectRunsheetDialog
                confirmButtonText="Select"
                open={showRunsheetModal}
                setOpen={setShowRunsheetModal}
                runsheets={runsheets}
                setCreateRunsheetDialogOpen={setShowCreateRunsheetModal}
                onSelectRunsheet={runsheet => {
                    if (runsheet) {
                        addToCollectionAndRunsheet(runsheet.id)
                        
                        setShowRunsheetModal(false)
                    }
                }}/>
            <UpsertRunsheet
                showAdd={showCreateRunsheetModal}
                showHideAdd={setShowCreateRunsheetModal}
                redirect={false}
                runsheetSaved={runsheet => {
                    addToCollectionAndRunsheet(runsheet.id)
                    
                    setShowCreateRunsheetModal(false)
                }} />
        </>
    )
    
}
